import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import type { AgentRunBucket } from '@/models/agentRunReport'
import { OTHER_LABEL, seriesColor } from './chartPalette'
import { formatCount } from './format'
import { formatPeriod } from './period'

const SURFACE = '#ffffff'

/** Runs per bucket, composed by agent — which agent is driving the volume. */
export function RunsByAgentChart({
  buckets,
  series,
  granularity,
}: {
  buckets: AgentRunBucket[]
  series: string[]
  granularity: string
}) {
  const data = useMemo(
    () =>
      buckets.map((bucket) => {
        const row: Record<string, string | number> = { period: bucket.period }
        for (const name of series) row[name] = 0
        for (const entry of bucket.byAgent) row[entry.agentSlug] = entry.runs
        return row
      }),
    [buckets, series]
  )

  if (!series.length) return <p className="text-ink-400 py-12 text-center text-sm">No runs in this range.</p>

  return (
    <ResponsiveContainer width="100%" height={360}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="#E4E3E0" strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="period"
          tickFormatter={(value: string) => formatPeriod(value, granularity)}
          tick={{ fill: '#666764', fontSize: 12 }}
          stroke="#D6D5D2"
          minTickGap={24}
        />
        <YAxis tick={{ fill: '#666764', fontSize: 12 }} stroke="#D6D5D2" width={48} />
        <Tooltip
          labelFormatter={(value) => formatPeriod(String(value), granularity, true)}
          formatter={(value) => formatCount(Number(value))}
          contentStyle={{ borderRadius: 8, borderColor: '#E4E3E0', fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {series.map((name, index) => (
          <Bar
            key={name}
            dataKey={name}
            stackId="runs"
            fill={seriesColor(series, name)}
            // A 2px surface-colored edge reads as a gap between segments.
            stroke={SURFACE}
            strokeWidth={2}
            radius={index === series.length - 1 ? [4, 4, 0, 0] : undefined}
            name={name === OTHER_LABEL ? OTHER_LABEL : name}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
